import { IonCol, IonIcon, IonRow, useIonToast } from '@ionic/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import TextMarquee from 'components/TextMarquee';

import { InsightValue, TimeFrameValue } from 'common/types';
import { heatBeanRatings, floormapJackpotHeatBeanRatings } from 'config/HeatBeanRatings';
import { parseValue, TOP_SLOTS_LENGTH } from 'common/include';
import useFavorites from 'common/reactHooks/useFavorites';
import usePlaylist, { isIncludedInPlaylist } from 'common/reactHooks/usePlaylist';
import { PlaylistIcon } from 'components/PlaylistIcon/PlaylistIcon';
import { FavoriteIcon } from 'components/FavoriteIcon/FavoriteIcon';
import { SelectCircleIcon } from 'components/SelectCircleIcon/SelectCircleIcon';
import { InfoIcon } from 'components/InfoIcon/InfoIcon';
import ChallengePendingCard from './components/ChallengePendingCard';
import {
  floormapTopHeatScale,
  getTopAndBottom,
  getOrderAndHeatKey,
  isJackpotInsight,
  mapNtileToFloormapHeat,
  setHeatKeyForAllItems,
  OrderByEnum,
  getCleanGameName,
  getCleanThemeName,
  toCapitalizedWords,
} from 'common/common';
import { ICasinoReport, ISearchInfo, ITrendsReport } from 'appRedux/models/casinoModels';
import { ICasinoSearchData, IState, useAppSelector } from 'appRedux/createStore';
import { isIncludedInFavorites } from 'pages/Favorites/components/LikeButton';
import {
  selectIsSlotOrderAscending,
  selectUserId,
  selectFavoritesList,
  selectPlaylist,
  selectCasinoId,
} from 'appRedux/selectors';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { CasinoService } from 'services/casino';
import { firstValueFrom } from 'rxjs';
import { bustCloakCache } from 'lib/cloakEgmsCache';
import { SLOT_CHECK_CASINO_ID, useCloakedMnums } from 'hooks/useCloakedMnums';
import { rejectCloaked } from 'lib/rejectCloaked';
import { featureFlags } from 'config/featureFlags';
import { ellipsisVertical } from 'ionicons/icons';
import {
  MACHINE_MENU_POPOVER_WIDTH,
  machineMenuBackdropStyle,
  machineMenuContainerStyle,
  machineMenuDividerStyle,
  machineMenuFilterItemStyle,
  machineMenuIconSlotStyle,
  machineMenuItemStyle,
} from 'components/MachineActionsMenu/menuStyles';
import {
  egmCardMeta,
  egmDetailId,
  egmDetailKey,
  insightCardName,
  insightCardTitle,
  cardInsightDisplay,
  insightCardValue,
  insightNtile,
  isInsightCardRow,
  insightCardBackground,
  matchesEgmDetailId,
  searchRowDetailId,
} from 'services/insightCasino';
import { setActiveMachinesCatalog } from 'lib/machineCatalog';

const ntileGradients = {
  1: 'linear-gradient(83.89deg, #07BDD1 1.68%, #018CE3 97.65%)',
  2: 'linear-gradient(83.89deg, #89DBDD 1.68%, #74C5EF 97.65%)',
  3: 'linear-gradient(84.88deg, #EBEBEB 5.81%, #FFFFFF 95.16%)',
  4: 'linear-gradient(85.08deg, #F485AE 6.1%, #FC4DAD 97.28%)',
  5: 'linear-gradient(85.08deg, #F04288 6.1%, #F60C8D 97.28%)',
};

type IHotListDataProps = {
  slotData?: ICasinoReport[];
  hotlist_detail: ICasinoReport[];
  trendComparisonMap: ITrendsReport[];
  total_count?: any;
  insightKey: InsightValue;
  timeframeKey: TimeFrameValue;
  listType: string;
  onClickSlot: (slotdata: ICasinoReport) => void;
  advancedViewInsights: InsightValue[];
  isAdvancedDisplayState: boolean;
  // When true (Favorites/Playlist/Filter views) every row in the map is pinned, so
  // number every row 1..N instead of just the top TOP_SLOTS_LENGTH.
  numberAllRows?: boolean;
  // Set when a map pin is single-tapped: scroll this slot's row into view. focusSeq
  // changes on every tap so re-tapping the same EGM re-triggers the scroll.
  focusedSlotNumber?: number | null;
  focusSeq?: number;
};
const HotListReport: React.FC<IHotListDataProps> = ({
  slotData,
  hotlist_detail,
  trendComparisonMap,
  insightKey,
  timeframeKey,
  listType,
  onClickSlot,
  advancedViewInsights,
  isAdvancedDisplayState,
  numberAllRows = false,
  focusedSlotNumber = null,
  focusSeq = 0,
}: IHotListDataProps) => {
  const { fetchUserFavorites, handleInsertFavorite, handleDeleteFavorite } = useFavorites({});
  const { fetchUserPlaylist, handleInsertPlaylist, handleDeletePlaylist } = usePlaylist();
  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);
  const slotCommands = SlotReduxCommandCreator(dispatch);
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const casinoSearchData = useSelector<IState>(
    (state) => state.app.persistedState.casinoSearchData
  ) as ICasinoSearchData | undefined;
  const { isComparing } = useAppSelector((state) => state.app.persistedState.compareSlotsState);
  const userId = useSelector(selectUserId);
  const isSlotOrderAscending = useSelector(selectIsSlotOrderAscending);
  const favoritesList = useSelector(selectFavoritesList);
  const playlist = useSelector(selectPlaylist);
  const casinoId = useSelector(selectCasinoId);
  const userGroups =
    (useSelector<IState>((state) => state.app.persistedState.userInfo?.groups) as string[]) || [];
  const [presentToast] = useIonToast();
  const isPlaylistEnabled = featureFlags.playlist;
  const { selectedSlots } = useAppSelector((state) => state.app.persistedState.slotState);
  const { selectedSlots: selectedCompareSlots } = useAppSelector(
    (state) => state.app.persistedState.compareSlotsState
  );

  const canCloakFromCard =
    userGroups.includes('slotcheck-enterprise-cloakTheEgms') &&
    Number(casinoId) !== SLOT_CHECK_CASINO_ID;
  const cloakedMnums = useCloakedMnums(casinoId);

  // MapStats subtracts cloaked EGMs from hotlist_detail before it gets here, so that
  // the drawer list and the map's pins are built from the same set. Re-filtering is
  // cheap and keeps this component correct on its own if it is handed a raw list.
  const visibleHotlist = useMemo(
    () => rejectCloaked(hotlist_detail, cloakedMnums),
    [hotlist_detail, cloakedMnums]
  );

  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const closeMenu = () => {
    setPopoverPosition(null);
    setActiveIndex(null);
  };

  const cloakFromCard = async () => {
    if (!canCloakFromCard) return;
    if (activeIndex === null) return;
    const row = visibleHotlist[activeIndex];
    closeMenu();
    if (!row || casinoId == null) return;
    const casinoIdStr = String(casinoId);
    const name = (getCleanGameName(row.game_name) || row.game_name || '').trim();
    const theme = (
      (row.game_theme || '').trim() ||
      String((row as ICasinoReport & { theme?: string }).theme || '').trim() ||
      (getCleanThemeName(row.game_name) || '').trim()
    );
    const body: { casinoId: string; mnum: string; name?: string; theme?: string } = {
      casinoId: casinoIdStr,
      // Cloak API expects machines.mnum (egmDetailId), never floorLocation/slot_number.
      mnum: egmDetailKey(row) || String((row as ICasinoReport & { mnum?: string }).mnum ?? ''),
    };
    if (name) body.name = name;
    if (theme) body.theme = theme;
    try {
      await firstValueFrom(casinoSvc.CloakEgm(body));
      bustCloakCache(casinoIdStr);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to cloak EGM.';
      presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message: String(msg),
      }).catch(() => null);
    }
  };

  // Long-press (>= 500ms) on an EGM card opens the EGM detail view.
  const LONG_PRESS_MS = 500;
  const longPressTimer = useRef<number | null>(null);
  const longPressTriggered = useRef(false);

  const clearLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const startLongPress = (slotData: ICasinoReport) => {
    clearLongPress();
    longPressTriggered.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      longPressTriggered.current = true;
      onClickSlot(slotData);
    }, LONG_PRESS_MS);
  };

  const getLongPressHandlers = (slotData: ICasinoReport) => ({
    onTouchStart: () => startLongPress(slotData),
    onTouchEnd: clearLongPress,
    onTouchMove: clearLongPress,
    onTouchCancel: clearLongPress,
    onMouseDown: () => startLongPress(slotData),
    onMouseUp: clearLongPress,
    onMouseLeave: clearLongPress,
  });

  useEffect(() => () => clearLongPress(), []);

  let AllTopAndBottomSlots: ICasinoReport[] = [];

  const hotListRowInsights = isAdvancedDisplayState ? advancedViewInsights : [insightKey];
  const isExploreV2 = featureFlags.getThemeVersion() !== 'v1';
  useEffect(() => {
    if (userId && casinoId) {
      fetchUserFavorites(userId, String(casinoId));
    }
  }, [fetchUserFavorites, userId, casinoId]);

  useEffect(() => {
    if (isPlaylistEnabled && userId && casinoId) {
      fetchUserPlaylist(userId, String(casinoId));
    }
  }, [fetchUserPlaylist, userId, casinoId, isPlaylistEnabled]);

  // Scroll the single-tapped EGM's row into view. We scroll the list's own ion-content
  // scroller (not scrollIntoView, which would also pan the drawer/page and hide the
  // header). Deferred a tick so the row exists after the selection re-render.
  useEffect(() => {
    if (focusSeq <= 0 || focusedSlotNumber == null) return;
    const handle = window.setTimeout(() => {
      const row = document.getElementById(`slot-row-${focusedSlotNumber}`);
      if (!row) return;
      const content = row.closest('ion-content') as
        | (HTMLElement & {
            getScrollElement?: () => Promise<HTMLElement>;
            scrollToPoint?: (x: number, y: number, duration?: number) => void;
          })
        | null;
      if (content?.getScrollElement && content.scrollToPoint) {
        content
          .getScrollElement()
          .then((scrollEl) => {
            const rowRect = row.getBoundingClientRect();
            const scrollRect = scrollEl.getBoundingClientRect();
            // Center the row in the visible list area.
            const delta =
              rowRect.top - scrollRect.top - (scrollRect.height / 2 - rowRect.height / 2);
            const top = Math.max(0, scrollEl.scrollTop + delta);
            content.scrollToPoint?.(0, top, 300);
          })
          .catch(() => {
            /* ignore — row may have unmounted */
          });
      } else {
        // Fallback: minimal scroll that won't jump ancestors.
        row.scrollIntoView({ block: 'nearest' });
      }
    }, 120);
    return () => window.clearTimeout(handle);
  }, [focusSeq, focusedSlotNumber]);

  // Bumped after module-catalog rehydrate so cards re-read catalog fallbacks.
  // Display id for insight cards prefers adaptDoc-joined row.slotNumber (same
  // machines match as game_name) — no new GetSearchData required for that path.
  const [catalogEpoch, setCatalogEpoch] = useState(0);

  useEffect(() => {
    if (!casinoId) return;
    // Search / catalog fallback only. Card display uses adaptDoc slotNumber on
    // GetCasinoInsights rows (casinoInsight.ts adaptDoc). Do not force a new
    // machines fetch when Redux already has this casino's GetSearchData.
    if (casinoSearchData && casinoSearchData.casinoId === casinoId) {
      setActiveMachinesCatalog(casinoSearchData.data ?? null);
      setCatalogEpoch((n) => n + 1);
      return;
    }
    const sub = casinoSvc.GetSearchData(casinoId as number).subscribe({
      next(resp) {
        if (resp) {
          const cleaned = resp.DataSet.map((item) => {
            const keepRaw =
              isInsightCardRow(item) ||
              (item as { source?: unknown }).source === 'insight';
            return {
              ...item,
              game_name: keepRaw ? item.game_name : getCleanGameName(item.game_name),
            };
          });
          casinoCommands.SetCasinoSearchData({
            casinoId: casinoId as number,
            data: cleaned,
          });
        }
      },
    });
    return () => sub.unsubscribe();
  }, [casinoId, casinoSearchData]);

  void catalogEpoch;

  if (slotData) {
    const { top10, bottom10 } = getTopAndBottom(slotData, insightKey);
    if (isSlotOrderAscending) {
      for (let index = 0; index < top10.length; index++) {
        const element = {
          ...top10[index],
          ...getOrderAndHeatKey(index, OrderByEnum.Desc, insightKey),
        };
        AllTopAndBottomSlots = [...AllTopAndBottomSlots, element];
      }
      if (insightKey === 'jackpots' || insightKey === 'jackpot_value') {
        AllTopAndBottomSlots = setHeatKeyForAllItems(AllTopAndBottomSlots, floormapTopHeatScale);
      }
    } else {
      for (let index = 0; index < bottom10.length; index++) {
        const reversedIndex = bottom10.length - 1 - index;
        const element = {
          ...bottom10[index],
          ...getOrderAndHeatKey(reversedIndex, OrderByEnum.Asc, insightKey),
        };
        AllTopAndBottomSlots.push(element);
      }
    }
  }
  if (selectedSlots.length > 0) {
    AllTopAndBottomSlots = [];
  }
  // Returns the correct heat color for a row — gold shades for jackpot insights
  // (when jackpotGoldColors flag is on), standard pink/warm scale otherwise.

  const menuBackgroundColors: Record<number, string> = {
    1: '#2BAFF6',
    2: '#74C5EF',
    3: '#FFFFFF',
    4: '#FB50AD',
    5: '#F60C8D',
    6: '#F60C8D',
  };

  const isHotlistJackpotRow = (jackpots: number | string | undefined): boolean => {
    return Number(jackpots) > 0;
  };

  const getMenuBackgroundColor = (ntile: number, jackpots: number): string => {
    if (isJackpotInsight(insightKey)) {
      return floormapJackpotHeatBeanRatings[mapNtileToFloormapHeat(ntile)]?.color;
    }
    if (isExploreV2 && jackpots > 0) {
      return '#FFBD60';
    }
    return menuBackgroundColors[ntile] ?? '#FFFFFF';
  };

  const getGradientRowColor = (
    rawNtile: number,
    insight: InsightValue,
    rowData: ICasinoReport
  ): string => {
    const ntile = insightNtile(rawNtile, insight, rowData);
    if (isJackpotInsight(insight)) {
      return floormapJackpotHeatBeanRatings[mapNtileToFloormapHeat(ntile)]?.color;
    }
    if (isExploreV2 && isHotlistJackpotRow(rowData.jackpots)) {
      return '#FFBD60';
    }
    if (ntile >= 1 && ntile <= 5) {
      return ntileGradients[ntile as keyof typeof ntileGradients];
    }
    return heatBeanRatings[ntile]?.color;
  };

  const getRowColor = (rawNtile: number, insight: InsightValue, rowData: ICasinoReport): string => {
    const ntile = insightNtile(rawNtile, insight, rowData);
    if (isJackpotInsight(insight)) {
      return floormapJackpotHeatBeanRatings[mapNtileToFloormapHeat(ntile)]?.color;
    }
    // if (isExploreV2 && isHotlistJackpotRow(rowData.jackpots)) {
    //   return '#FFBD60';
    // }
    if (ntile >= 1 && ntile <= 5) {
      return ntileGradients[ntile as keyof typeof ntileGradients];
    }
    return heatBeanRatings[ntile]?.color;
  };

  const getRowColorWithOpacity = (color: string, opacity = 0.5): string => {
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const normalized =
        hex.length === 3 || hex.length === 4
          ? hex
            .split('')
            .map((char) => char + char)
            .join('')
          : hex.length === 6 || hex.length === 8
            ? hex.slice(0, 6)
            : hex;
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    if (color.startsWith('rgb(')) {
      return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
    }

    if (color.startsWith('rgba(')) {
      return color.replace(
        /rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/,
        `rgba($1, $2, $3, ${opacity})`
      );
    }

    return color;
  };

  const getTextColor = (ntile: number, insight: InsightValue): string => {
    const colorMap: Record<number, string> = {
      6: '#FFFFFF',
      5: '#FFFFFF',
      4: '#FFFFFF',
      3: '#636363',
      2: '#0B5888',
      1: '#05355D',
    };

    if (isJackpotInsight(insight)) {
      if (ntile === 6) return '#4C4C4C';
      if (ntile === 5) return '#424242';
      if (ntile === 4) return '#302D2D';
    }

    return colorMap[ntile] || '#000000';
  };

  type ReturnComparisonArrowDirection = 'right' | 'upper-right' | 'lower-right';

  const getReturnComparisonArrowDirection = (
    rowData: ICasinoReport,
    timeframeKey: TimeFrameValue
  ): ReturnComparisonArrowDirection | undefined => {
    if (insightKey !== 'return') {
      return undefined;
    }

    const trendData = trendComparisonMap.find((trend: ITrendsReport) =>
      matchesEgmDetailId(rowData, (trend as { mnum?: string }).mnum ?? trend.slot_number)
    );
    if (!trendData) {
      return undefined;
    }

    let comparedValue: number | undefined;
    switch (timeframeKey) {
      case 'months1':
        comparedValue = trendData.d_insight_value;
        break;
      case 'weeks1':
        comparedValue = trendData.d_insight_value;
        break;
      case 'hours24':
        comparedValue = trendData.h4_insight_value;
        break;
      case 'hours8':
        comparedValue = trendData.h4_insight_value;
        break;
      default:
        return undefined;
    }

    // Trend values arrive as fractions; insight rows carry return as a percent.
    const currentValue = isInsightCardRow(rowData)
      ? Number(insightCardValue(rowData, 'return') ?? NaN)
      : Number(rowData.return);
    if (Number.isNaN(currentValue) || Number.isNaN(comparedValue ?? NaN)) return undefined;

    const thresholdBase = Math.abs(currentValue) || Math.abs(comparedValue) || 1;
    if (Math.abs(currentValue - comparedValue) <= thresholdBase * 0.1) {
      return 'right';
    }

    return currentValue > comparedValue ? 'lower-right' : 'upper-right';
  };

  const getHeatBean = (
    dataItem: ICasinoReport,
    insightKey: InsightValue
  ): React.ReactNode | null => {
    if (hotlist_detail?.length > 0) {
      if (hotlist_detail?.find((x: ICasinoReport) => matchesEgmDetailId(x, egmDetailId(dataItem)))) {
        const stats = dataItem[(insightKey + '_ntile') as keyof ICasinoReport];
        return (
          <div className="box-bean">
            <div
              className="box-indicator"
              style={{ background: getRowColor(Number(stats), insightKey, dataItem) }}
            />
          </div>
        );
      }
    }

    return null;
  };

  const renderCol = (
    rowData: ICasinoReport,
    insightKey: InsightValue,
    index: number
  ): React.ReactNode => {
    const unparsedValue = ['sleeper_slot_rating', 'volatility_rating'].includes(insightKey)
      ? rowData[`${insightKey}_ntile`]
      : rowData[insightKey];
    // Insight rows: only `return` (a percent from the API) takes the percent
    // path; every other key keeps the original parseValue path, ntile included.
    const parsedValue = cardInsightDisplay(rowData, insightKey, unparsedValue, parseValue);
    const isJackpotsInsight = insightKey === 'jackpot_value';
    const stats = rowData[(insightKey + '_ntile') as keyof ICasinoReport];
    const backgroundColor = getRowColor(Number(stats), insightKey, rowData);
    const menuBackgroundColor = getMenuBackgroundColor(Number(stats), Number(rowData.jackpots));
    const textColor = isExploreV2 ? getTextColor(Number(stats), insightKey) : undefined;
    const comparisonArrowDirection = isExploreV2
      ? getReturnComparisonArrowDirection(rowData, timeframeKey)
      : undefined;

    const jackpotTier =
      isJackpotsInsight && listType !== 'myJackpot'
        ? mapNtileToFloormapHeat(Number(stats))
        : null;
    const jackpotTierClass =
      jackpotTier === 5
        ? 'jackpot-pill--tier-top'
        : jackpotTier === 4
          ? 'jackpot-pill--tier-mid'
          : jackpotTier === 3
            ? 'jackpot-pill--tier-low'
            : null;
    const isJackpotPill = jackpotTierClass !== null;

    const rowValue = (
      <span
        className={clsx('row-insight-value', isJackpotPill && 'jackpot-pill', jackpotTierClass)}
        style={{
          background: isExploreV2 && !isJackpotPill ? backgroundColor : undefined,
          color: isJackpotPill ? undefined : textColor,
        }}
      >
        <div className="row-insight-value__text">{parsedValue}</div>

        {isJackpotsInsight && listType !== 'myJackpot' && (
          <span className="jackpot_number"> | {rowData['jackpots']}</span>
        )}
        {isExploreV2 && insightKey === 'return' && comparisonArrowDirection && (
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={clsx('arrow-icon', `arrow-${comparisonArrowDirection}`)}
            style={{
              borderRadius: '4px',
              transform:
                comparisonArrowDirection === 'upper-right'
                  ? 'rotate(-45deg)'
                  : comparisonArrowDirection === 'lower-right'
                    ? 'rotate(45deg)'
                    : comparisonArrowDirection === 'right'
                      ? 'rotate(0deg)'
                      : undefined,
            }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.22174 3.22172C7.51744 -1.07392 14.4817 -1.0739 18.7774 3.22172C23.0731 7.51742 23.0731 14.4817 18.7774 18.7774C14.4817 23.0731 7.51746 23.0731 3.22174 18.7774C-1.07389 14.4817 -1.07394 7.51741 3.22174 3.22172ZM13.5733 5.68363C13.1828 5.29315 12.5498 5.29322 12.1592 5.68363C11.7687 6.07416 11.7687 6.70717 12.1592 7.09769L15.251 10.1895H3.74714C3.195 10.1895 2.74738 10.6374 2.74714 11.1895C2.74714 11.7418 3.19485 12.1895 3.74714 12.1895H15.252L12.1592 15.2823C11.7689 15.6728 11.7688 16.3059 12.1592 16.6963C12.5497 17.0866 13.1828 17.0866 13.5733 16.6963L18.3731 11.8975C18.5682 11.7023 18.6651 11.4463 18.6651 11.1905C18.6651 11.1585 18.6642 11.1266 18.6612 11.0948C18.6521 10.9991 18.6284 10.9048 18.5919 10.8145C18.5431 10.6939 18.4708 10.5802 18.3731 10.4825C18.3694 10.4787 18.3652 10.4754 18.3614 10.4717L13.5733 5.68363Z"
              fill={textColor}
            />
          </svg>
        )}
      </span>
    );

    return (
      <div
        className={clsx(
          'hot-list-row',
          !isAdvancedDisplayState ? 'list-portrait' : '',
          insightKey === 'jackpot_value' ? 'jkpt-column' : ''
        )}
        key={`list-row-${
          isAdvancedDisplayState ? 'is-advanced-view' : 'is-standard-view'
        }-${insightKey}${rowData.slot_number}-${index}`}
      >
        <div className="game-data-td-report">{rowValue}</div>

        <>
          {!isAdvancedDisplayState && (
            <>
              <IonIcon
                icon={ellipsisVertical}
                className="menu-icon"
                style={{ color: isExploreV2 ? menuBackgroundColor : undefined }}
                onClick={(e) => {
                  e.stopPropagation();
                  const targetEl = e.currentTarget as unknown as HTMLElement;
                  const rect = targetEl.getBoundingClientRect();
                  const menuItemCount = (isPlaylistEnabled ? 5 : 4) + (canCloakFromCard ? 1 : 0);
                  const popoverHeight = menuItemCount * 48 + 2 * 13 + 16;
                  const safeMargin = 16;

                  let x = rect.left - MACHINE_MENU_POPOVER_WIDTH - 4;
                  if (x < safeMargin) x = safeMargin;

                  let y = rect.top;
                  if (y + popoverHeight + safeMargin > window.innerHeight) {
                    y = rect.bottom - popoverHeight;
                    if (y < safeMargin) y = safeMargin;
                  }

                  setPopoverPosition({ x, y });
                  setActiveIndex(index);
                }}
              />
              <span
                className="menu-icon-bubble"
                style={{
                  backgroundColor: isExploreV2 ? menuBackgroundColor : undefined,
                  color: isExploreV2 ? '#fff' : undefined,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // padding: isExploreV2 ? '8px' : undefined,
                }}
              ></span>
            </>
          )}
          {isAdvancedDisplayState && getHeatBean(rowData, insightKey)}
        </>
        {!isAdvancedDisplayState && !isExploreV2 && (
          <div
            style={{
              backgroundColor: getRowColor(Number(stats), insightKey, rowData),
            }}
            className="menu-icon-vertical"
          />
        )}
      </div>
    );
  };

  const isSlotSelected = (slotNumber: number): boolean => {
    return selectedCompareSlots.includes(slotNumber);
  };

  const handleMenuAction = (
    action: 'favorite' | 'view' | 'playlist' | 'filterTitle' | 'filterTheme'
  ) => {
    if (activeIndex === null) return;
    const selectedItem = visibleHotlist[activeIndex];
    if (!selectedItem) return;
    if (action === 'favorite') {
      if (userId && casinoId) {
        const slotNumber = egmDetailKey(selectedItem);
        if (!slotNumber) return;
        if (isIncludedInFavorites(slotNumber, favoritesList)) {
          handleDeleteFavorite(slotNumber, userId, String(casinoId), true);
        } else {
          handleInsertFavorite(slotNumber, userId, String(casinoId), true);
        }
      }
    } else if (action === 'playlist') {
      if (userId && casinoId) {
        const slotNumber = egmDetailKey(selectedItem);
        if (!slotNumber) return;
        const casinoIdStr = String(casinoId);
        if (isIncludedInPlaylist(slotNumber, playlist)) {
          handleDeletePlaylist(slotNumber, userId, casinoIdStr, true);
        } else {
          handleInsertPlaylist(slotNumber, userId, casinoIdStr, true);
        }
      }
    } else if (action === 'view') {
      onClickSlot(selectedItem);
    } else if (action === 'filterTitle' || action === 'filterTheme') {
      applyFilter(selectedItem, action === 'filterTitle' ? 'title' : 'theme');
    }

    closeMenu();
  };

  const applyFilter = (selectedItem: ICasinoReport, kind: 'title' | 'theme') => {
    const data: ISearchInfo[] = casinoSearchData?.data ?? [];
    const titleKey = getCleanGameName(selectedItem.game_name);
    const themeKey =
      (selectedItem.game_theme && selectedItem.game_theme.trim()) ||
      getCleanThemeName(selectedItem.game_name);

    let filterName = '';
    let matches: ISearchInfo[] = [];

    if (kind === 'title') {
      filterName = titleKey;
      matches = data.filter((d) => d.game_name === titleKey);
    } else {
      if (!themeKey) return;
      filterName = themeKey;
      matches = data.filter((d) => d.theme === themeKey);
    }

    // Insight search rows: mnum. Legacy: slot_number. Never floorLocation.
    const slotNumbers = matches
      .map((m) => searchRowDetailId(m))
      .filter((n): n is number => n !== null);
    const selfId = egmDetailId(selectedItem);
    if (slotNumbers.length === 0 && selfId !== null) {
      slotNumbers.push(selfId);
    }

    slotCommands.SetSearchMode(true);
    // A Game Name/Theme selection is never a floor-map bank filter — clear the
    // flag so downstream views treat this as a search filter.
    slotCommands.SelectBankFilter(false);
    slotCommands.SelectedSlotsName(filterName);
    slotCommands.SelectedSlots(slotNumbers.map(String) as unknown as string[]);
  };

  const gameNameColor = isExploreV2 ? '#fff' : '#36618E';
  return (
    <div className="hotlist-report">
      <div className={'slot-report'}>
        {/* Pending "personal invited" challenges surface as a gold card in the
            first EGM slot of the standard hot list. */}
        {isExploreV2 && listType === 'standard' && <ChallengePendingCard />}
        {visibleHotlist.map((eachitem, index) => {
          const rowColor = getRowColor(
            Number(eachitem[(insightKey + '_ntile') as keyof ICasinoReport]),
            insightKey,
            eachitem
          );
          const cardMeta = egmCardMeta(eachitem);
          const isInsightCard = isInsightCardRow(eachitem);
          // Insight: mnum; legacy: slot_number. Never floorLocation for APIs/favorites/compare.
          const detailId = egmDetailId(eachitem);
          const detailKey = egmDetailKey(eachitem);
          const rowGradientColor = getGradientRowColor(
            Number(eachitem[(insightKey + '_ntile') as keyof ICasinoReport]),
            insightKey,
            eachitem
          );

          const isJackpotRow = isHotlistJackpotRow(eachitem.jackpots);

          const rowBackground = `linear-gradient(90deg, rgba(29, 33, 46, 0) 0%, rgba(29, 33, 46, 0) 70%, ${getRowColorWithOpacity(
            rowGradientColor,
            0.5
          )} 100%)`;
          const rowStyle = isExploreV2
            ? {
              position: isJackpotRow ? 'relative' : undefined,
              background: isJackpotRow
                ? `url("assets/images/coin-report.png") right center / auto 100% no-repeat, ${rowBackground}`
                : insightCardBackground(
                  eachitem,
                  insightKey,
                  Number(eachitem[(insightKey + '_ntile') as keyof ICasinoReport]),
                  isJackpotRow
                ) ?? rowBackground,
              overflow: isJackpotRow ? 'hidden' : undefined,
            }
            : undefined;

          return (
            <IonRow
              className={clsx(
                'slot-table-row ion-align-items-center',
                detailId !== null && isSlotSelected(detailId)
                  ? 'favorites-container__list__item--active'
                  : ''
              )}
              style={rowStyle}
              // Stable per-EGM key (mnum for insight rows, slot_number for legacy)
              // so added data never makes React reuse a row for a different EGM
              // (cards visibly swapping under the finger). Index only as a
              // fallback for a row with no id.
              key={`slot-report-${
                isAdvancedDisplayState ? 'is-advanced-view' : 'is-standard-view'
              }-${detailKey || `idx-${index}`}`}
              // onClick={() => {
              //   if (isProUser && !isComparing) {
              //     slotCompareCommands.toggleSelectedSlot({ slotNumber: detailId });
              //   }
              // }}
              id={`slot-row-${detailId ?? index}`}
              {...getLongPressHandlers(eachitem)}
            >
              {isExploreV2 && isJackpotRow && <span className="jkpt-title">JACKPOT</span>}
              <IonCol
                size={isAdvancedDisplayState ? '3' : '7.5'}
                // Standard view: name column fills leftover width up to the pill
                // (see .game-card-col in _m-report-container.scss).
                className={clsx(!isAdvancedDisplayState && 'game-card-col')}
              >
                <div className="game-card-info">
                  {!isComparing && detailId !== null && (
                    <span className="compare-checkbox-wrap">
                      <input
                        type="checkbox"
                        aria-label="Select slot"
                        className={clsx('compare-checkbox', 'compare-checkbox--circle')}
                        checked={isSlotSelected(detailId)}
                        onChange={() =>
                          slotCompareCommands.toggleSelectedSlot({
                            slotNumber: detailId,
                          })
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <SelectCircleIcon
                        checked={isSlotSelected(detailId)}
                        className="compare-checkbox__visual"
                      />
                    </span>
                  )}
                  <div className="game-card__center">
                    {(numberAllRows || index < TOP_SLOTS_LENGTH) && (
                      <div className="game-card__index">{index + 1}</div>
                    )}
                    {!numberAllRows && index >= TOP_SLOTS_LENGTH && (
                      <div className="game-card__index-empty" />
                    )}
                    <div className="game-card__info">
                      <TextMarquee
                        className={clsx('game-card__name', isJackpotRow && 'name-in-jackpot')}
                        style={{
                          color: gameNameColor,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // A completed long-press already opened the detail view; skip the tap.
                          if (longPressTriggered.current) {
                            longPressTriggered.current = false;
                            return;
                          }
                          onClickSlot(eachitem);
                        }}
                        text={
                          isInsightCard
                            ? insightCardName(eachitem)
                            : toCapitalizedWords(getCleanGameName(eachitem?.game_name))
                        }
                      />
                      <div className="game-card__theme">
                        {isInsightCard
                          ? insightCardTitle(eachitem)
                          : toCapitalizedWords(getCleanThemeName(eachitem?.game_name))}
                      </div>

                      <div className="game-card__meta">
                        {/* **{getLastTwoDigits(eachitem?.slot_number)} */}

                        {isExploreV2 && (
                          <>
                            {cardMeta.number}
                            {cardMeta.showSeparator && <span className="dot-box">-</span>}
                            {cardMeta.bankLabel}
                            {isPlaylistEnabled && (
                              <span
                                role="button"
                                aria-label={
                                  isIncludedInPlaylist(detailKey, playlist)
                                    ? 'Remove from Playlist'
                                    : 'Add to Playlist'
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (userId && casinoId) {
                                    if (
                                      isIncludedInPlaylist(detailKey, playlist)
                                    ) {
                                      handleDeletePlaylist(
                                        detailKey,
                                        userId,
                                        String(casinoId)
                                      );
                                    } else {
                                      handleInsertPlaylist(
                                        detailKey,
                                        userId,
                                        String(casinoId)
                                      );
                                    }
                                  }
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  marginLeft: 4,
                                }}
                              >
                                <PlaylistIcon
                                  size={16}
                                  className="playlist-icon"
                                  filled={isIncludedInPlaylist(
                                    detailKey,
                                    playlist
                                  )}
                                />
                              </span>
                            )}
                            <span
                              role="button"
                              aria-label={
                                isIncludedInFavorites(
                                  detailKey,
                                  favoritesList
                                )
                                  ? 'Remove from Favorites'
                                  : 'Add to Favorites'
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (userId && casinoId) {
                                  if (
                                    isIncludedInFavorites(
                                      detailKey,
                                      favoritesList
                                    )
                                  ) {
                                    handleDeleteFavorite(
                                      detailKey,
                                      userId,
                                      String(casinoId)
                                    );
                                  } else {
                                    handleInsertFavorite(
                                      detailKey,
                                      userId,
                                      String(casinoId)
                                    );
                                  }
                                }
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                marginLeft: 4,
                              }}
                            >
                              <FavoriteIcon
                                className="favorite-icon"
                                filled={isIncludedInFavorites(
                                  detailKey,
                                  favoritesList
                                )}
                              />
                            </span>
                          </>
                        )}

                        {!isExploreV2 && (
                          <>
                            {cardMeta.number}
                            {cardMeta.showSeparator && <span className="dot-box">•</span>}
                            {cardMeta.bankLabel}
                            {isPlaylistEnabled && (
                              <span
                                role="button"
                                aria-label={
                                  isIncludedInPlaylist(detailKey, playlist)
                                    ? 'Remove from Playlist'
                                    : 'Add to Playlist'
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (userId && casinoId) {
                                    if (
                                      isIncludedInPlaylist(detailKey, playlist)
                                    ) {
                                      handleDeletePlaylist(
                                        detailKey,
                                        userId,
                                        String(casinoId)
                                      );
                                    } else {
                                      handleInsertPlaylist(
                                        detailKey,
                                        userId,
                                        String(casinoId)
                                      );
                                    }
                                  }
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  marginLeft: 4,
                                }}
                              >
                                <PlaylistIcon
                                  size={16}
                                  className="playlist-icon"
                                  filled={isIncludedInPlaylist(
                                    detailKey,
                                    playlist
                                  )}
                                />
                              </span>
                            )}
                            <span
                              role="button"
                              aria-label={
                                isIncludedInFavorites(
                                  detailKey,
                                  favoritesList
                                )
                                  ? 'Remove from Favorites'
                                  : 'Add to Favorites'
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (userId && casinoId) {
                                  if (
                                    isIncludedInFavorites(
                                      detailKey,
                                      favoritesList
                                    )
                                  ) {
                                    handleDeleteFavorite(
                                      detailKey,
                                      userId,
                                      String(casinoId)
                                    );
                                  } else {
                                    handleInsertFavorite(
                                      detailKey,
                                      userId,
                                      String(casinoId)
                                    );
                                  }
                                }
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                marginLeft: 4,
                              }}
                            >
                              <FavoriteIcon
                                className="favorite-icon"
                                filled={isIncludedInFavorites(
                                  detailKey,
                                  favoritesList
                                )}
                              />
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </IonCol>
              {hotListRowInsights.map((insight: InsightValue) =>
                renderCol(eachitem, insight, index)
              )}
            </IonRow>
          );
        })}

        {popoverPosition && activeIndex !== null && createPortal(
          <>
            <div onClick={closeMenu} style={machineMenuBackdropStyle} />
            <div
              role="menu"
              className="machine-menu-actions"
              onClick={(e) => e.stopPropagation()}
              style={{
                ...machineMenuContainerStyle,
                left: popoverPosition.x,
                top: popoverPosition.y,
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => handleMenuAction('favorite')}
                style={machineMenuItemStyle}
              >
                <span style={machineMenuIconSlotStyle} aria-hidden="true">
                  <FavoriteIcon variant="menu" />
                </span>
                {isIncludedInFavorites(
                  egmDetailKey(visibleHotlist[activeIndex]),
                  favoritesList
                )
                  ? 'Remove from Favorites'
                  : 'Save to Favorites'}
              </button>
              {isPlaylistEnabled && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleMenuAction('playlist')}
                  style={machineMenuItemStyle}
                >
                  <span style={machineMenuIconSlotStyle} aria-hidden="true">
                    <PlaylistIcon filled size={14} color="#94A0AA" />
                  </span>
                  {isIncludedInPlaylist(
                    egmDetailKey(visibleHotlist[activeIndex]),
                    playlist
                  )
                    ? 'Remove from Playlist'
                    : 'Save to Playlist'}
                </button>
              )}
              <hr style={machineMenuDividerStyle} />
              <button
                type="button"
                role="menuitem"
                onClick={() => handleMenuAction('view')}
                style={machineMenuItemStyle}
              >
                <span style={machineMenuIconSlotStyle} aria-hidden="true">
                  <InfoIcon />
                </span>
                View Details
              </button>
              {(() => {
                const activeRow = visibleHotlist[activeIndex];
                const hasTitle = !!getCleanGameName(activeRow?.game_name);
                const hasTheme = !!(
                  (activeRow?.game_theme && activeRow.game_theme.trim()) ||
                  getCleanThemeName(activeRow?.game_name)
                );
                if (!hasTitle && !hasTheme) return null;
                return (
                  <>
                    <hr style={machineMenuDividerStyle} />
                    {hasTitle && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleMenuAction('filterTitle')}
                        style={machineMenuFilterItemStyle}
                      >
                        <span style={machineMenuIconSlotStyle} aria-hidden="true">
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <circle
                                cx="8"
                                cy="8"
                                r="7.46667"
                                stroke="white"
                                strokeWidth="1.06667"
                              />
                            </svg>
                            <span
                              style={{
                                position: 'absolute',
                                color: '#FFF',
                                textAlign: 'center',
                                fontFamily: 'Rufina, serif',
                                fontSize: 11.635,
                                fontWeight: 400,
                                lineHeight: 'normal',
                              }}
                            >
                              T
                            </span>
                          </span>
                        </span>
                        Filter by Title
                      </button>
                    )}
                    {hasTheme && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleMenuAction('filterTheme')}
                        style={machineMenuFilterItemStyle}
                      >
                        <span style={machineMenuIconSlotStyle} aria-hidden="true">
                          <span
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 6,
                              border: '1px solid rgba(255, 255, 255, 0.70)',
                            }}
                          />
                        </span>
                        Filter by Theme
                      </button>
                    )}
                  </>
                );
              })()}
              {canCloakFromCard && (
                <>
                  <hr style={machineMenuDividerStyle} />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void cloakFromCard()}
                    style={machineMenuItemStyle}
                  >
                    <span style={machineMenuIconSlotStyle} aria-hidden="true" />
                    Cloak EGM
                  </button>
                </>
              )}
            </div>
          </>,
          document.body
        )}
      </div>
    </div>
  );
};

export default HotListReport;
